import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, CheckCircle2, Info, Loader2 } from 'lucide-react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { useToast } from '@/hooks/use-toast';
import { getBroadcastCalendar } from '@/services/api/broadcast';
import {
    importarRotinaDeData,
    mensagemDeErro,
    type Rotina,
    type RotinaImportResultado,
} from '@/services/api/rotinas';
import { diaBRT, limiteUtcDoDiaBRT } from '@/pages/Meta/Broadcast/utils/calendarUtils';
import { DIAS_MINUSCULOS, DIAS_SEMANA, meuIdSeAdmin } from './rotinaUtils';

function diaDaSemana(data: string): number | undefined {
    const [a, m, d] = data.split('-').map(Number);
    if (!a || !m || !d) return undefined;
    return new Date(a, m - 1, d).getDay();
}

const porExtenso = (data: string) => data.split('-').reverse().join('/');

function Metrica({ rotulo, valor, alerta }: { rotulo: string; valor: string; alerta?: boolean }) {
    return (
        <div className="flex flex-col gap-0.5 px-4 py-3">
            <span className={`text-xl font-bold tabular-nums ${alerta ? 'text-amber-700' : ''}`}>{valor}</span>
            <span className="text-xs text-muted-foreground">{rotulo}</span>
        </div>
    );
}

function Resultado({ r, data, dia }: { r: RotinaImportResultado; data: string; dia: number }) {
    const [verIds, setVerIds] = useState(false);
    const soma = (k: 'traducao_sem_idioma' | 'paginas_fora_do_dia' | 'paginas_sem_page_id' | 'paginas_canceladas') =>
        r.grupos.reduce((s, g) => s + g[k], 0);
    const total = {
        idioma: soma('traducao_sem_idioma'),
        fora: soma('paginas_fora_do_dia'),
        semId: soma('paginas_sem_page_id'),
        canceladas: soma('paginas_canceladas'),
    };
    const broadcastsDia = r.grupos.reduce((s, g) => s + g.paginas, 0);
    const diaDaData = diaDaSemana(data);
    const divergeDoDia = diaDaData !== undefined && diaDaData !== dia;

    // Só o descarte que o gestor pode resolver. Grupo de outro dia a busca traz
    // pela margem de fuso e devolve — não é assunto dele.
    const problemas = r.grupos_ignorados.filter((g) => !g.outro_dia);

    const idiomasProntos = r.grupos.reduce((m, g) => Math.max(m, g.idiomas_traduzidos), 0);
    const tudoTraduzido = r.linhas_sem_traducao === 0 && idiomasProntos > 0;

    // Coluna de contagem só aparece quando tem o que contar: dez linhas de zero
    // ensinam o olho a pular a tabela, e é nela que o número diferente de zero
    // precisaria gritar.
    const colunas = [
        { chave: 'linhas_sem_traducao', titulo: 'Sem trad.', total: r.linhas_sem_traducao,
          nota: 'broadcasts do grupo sem tradução guardada' },
        { chave: 'traducao_sem_idioma', titulo: 'Trad. sem idioma', total: total.idioma,
          nota: 'traduções sem idioma de destino, ficam fora do mapa' },
        { chave: 'paginas_fora_do_dia', titulo: 'Fora do dia', total: total.fora,
          nota: 'páginas agendadas em outro dia local, não entraram no grupo' },
        { chave: 'paginas_sem_page_id', titulo: 'Sem page_id', total: total.semId,
          nota: 'páginas sem identificador no broadcast, não entraram no grupo' },
        { chave: 'paginas_canceladas', titulo: 'Canceladas', total: total.canceladas,
          nota: 'broadcasts cancelados no dia de origem: ficam de fora para a Rotina não os recriar todo dia' },
    ] as const;
    const visiveis = colunas.filter((c) => c.total > 0);

    return (
        <div className="space-y-4 text-sm">
            <div className="flex items-start gap-3">
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-emerald-100 text-emerald-800">
                    <CheckCircle2 className="h-5 w-5" />
                </span>
                <div>
                    <p className="font-semibold">Rotina criada — desligada</p>
                    <p className="text-xs text-muted-foreground">
                        {DIAS_SEMANA[dia]}, copiada de {porExtenso(data)}. Desligada não materializa nada: confira os
                        pontos abaixo e ligue quando estiver certa.
                    </p>
                </div>
            </div>

            <div className="grid grid-cols-2 divide-x divide-y rounded-lg border sm:grid-cols-4 sm:divide-y-0">
                <Metrica rotulo="grupos copiados" valor={String(r.grupos_importados)} />
                <Metrica rotulo="broadcasts / dia" valor={broadcastsDia.toLocaleString('pt-BR')} />
                {tudoTraduzido ? (
                    <Metrica rotulo="idiomas prontos" valor={String(idiomasProntos)} />
                ) : (
                    <Metrica rotulo="sem tradução" valor={r.linhas_sem_traducao.toLocaleString('pt-BR')} alerta={r.linhas_sem_traducao > 0} />
                )}
            </div>

            {(divergeDoDia || problemas.length > 0 || r.linhas_sem_traducao > 0 || r.avisos.length > 0) && (
                <div className="space-y-2">
                    <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Precisa de atenção</h3>

                    {divergeDoDia && (
                        <div className="rounded-lg border border-orange-300 bg-orange-50 p-3 text-xs text-orange-900">
                            <strong>
                                {porExtenso(data)} é {DIAS_MINUSCULOS[diaDaData]}, mas a Rotina vale para{' '}
                                {DIAS_MINUSCULOS[dia]}.
                            </strong>{' '}
                            Confira as horas: o dia local de cada página é o que decide.
                        </div>
                    )}

                    {problemas.length > 0 && (
                        <div className="rounded-lg border border-amber-300 bg-amber-50 text-amber-900">
                            <button
                                type="button"
                                aria-expanded={verIds}
                                onClick={() => setVerIds((v) => !v)}
                                className="flex w-full items-center gap-2 px-3 py-2.5 text-left text-xs"
                            >
                                <span className="flex-1">
                                    <strong className="tabular-nums">{problemas.length} grupo(s) não entraram no molde</strong>{' '}
                                    {/* Sem "ficaram de fora": o motivo pode ser uma decisão
                                        do próprio gestor na origem (grupo cancelado), e o
                                        texto anterior soava como falha da importação. */}
                                    <span className="opacity-80">— veja o motivo de cada um</span>
                                </span>
                                <span className="font-medium opacity-80">{verIds ? 'Ocultar' : 'Ver motivos'}</span>
                            </button>
                            {verIds && (
                                <ul className="space-y-1 border-t border-amber-300 px-3 py-2 text-xs">
                                    {problemas.map((g) => (
                                        <li key={g.group_broadcast_id}>
                                            <span className="rounded bg-amber-100 px-1.5 py-0.5 font-mono tabular-nums">
                                                #{g.group_broadcast_id}
                                            </span>{' '}
                                            {g.titulo && <span className="font-medium">{g.titulo} · </span>}
                                            <span className="opacity-80">{g.motivo}</span>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>
                    )}

                    {r.linhas_sem_traducao > 0 && (
                            <p className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-xs text-amber-900">
                            <strong className="tabular-nums">{r.linhas_sem_traducao} broadcast(s) sem tradução guardada</strong>{' '}
                            — a Rotina não terá o texto deles por idioma. Grupos de utility não entram nesta
                            conta: o texto deles vive no template aprovado por página.
                        </p>
                    )}

                    {/* Os avisos vêm do backend já escritos, grupo por grupo. Nenhum é resumido aqui. */}
                    {r.avisos.length > 0 && (
                        <ul className="list-disc space-y-0.5 rounded-lg border border-amber-300 bg-amber-50 p-3 pl-7 text-xs text-amber-900">
                            {r.avisos.map((a, i) => <li key={i}>{a}</li>)}
                        </ul>
                    )}
                </div>
            )}

            {/* Grupo de outro dia nunca chegou a ser candidato: a busca varre uma
                margem de fuso e a faxina é o funcionamento normal. Anunciar isso só
                levantava pergunta sobre um número que não é do gestor. O backend
                continua devolvendo `outro_dia` — é o que permite distinguir isto do
                descarte que importa, logo acima. */}

            <div className="space-y-2">
                <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Grupos copiados</h3>
                <div className="overflow-x-auto rounded-lg border">
                    <table className="w-full text-xs">
                        <thead>
                            <tr className="border-b bg-muted/50 text-left text-muted-foreground">
                                <th className="px-2 py-1.5 font-semibold">Grupo</th>
                                <th className="px-2 font-semibold">Hora</th>
                                <th className="px-2 text-right font-semibold">Págs</th>
                                <th className="px-2 text-right font-semibold">Idiomas</th>
                                {visiveis.map((c) => (
                                    <th key={c.chave} className="px-2 text-right font-semibold">{c.titulo}</th>
                                ))}
                            </tr>
                        </thead>
                        <tbody className="tabular-nums">
                            {r.grupos.map((g, i) => (
                                <tr key={`${g.hora_local}-${i}`} className="border-b last:border-0">
                                    <td className="max-w-[14rem] truncate px-2 py-1" title={g.titulo_modelo}>
                                        {g.titulo_modelo}
                                        {/* Utility não traduz: a coluna "Sem trad." fica zerada
                                            de propósito, e sem esta marca isso pareceria dado
                                            faltando. */}
                                        {g.utility && (
                                            <span className="ml-1.5 rounded bg-muted px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground">
                                                utility
                                            </span>
                                        )}
                                    </td>
                                    <td className="px-2 font-medium">{g.hora_local}</td>
                                    <td className="px-2 text-right">{g.paginas}</td>
                                    <td className={`px-2 text-right ${g.idiomas_traduzidos ? '' : 'text-muted-foreground'}`}>{g.idiomas_traduzidos}</td>
                                    {visiveis.map((c) => (
                                        <td key={c.chave} className={`px-2 text-right ${g[c.chave] ? 'font-medium text-amber-700' : ''}`}>
                                            {g[c.chave]}
                                        </td>
                                    ))}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
                {/* Sem legenda quando não há coluna de ressalva: explicar a ausência
                    de problema é o próprio ruído que se quis tirar. */}
                {visiveis.length > 0 && (
                    <ul className="space-y-0.5 text-xs text-muted-foreground">
                        {visiveis.map((c) => (
                            <li key={c.chave}><b>{c.titulo}</b>: {c.nota} (total {c.total}).</li>
                        ))}
                    </ul>
                )}
            </div>
        </div>
    );
}

export default function ImportarDiaDialog({
    open,
    onOpenChange,
    rotinas,
    diaInicial,
    onAbrirDia,
}: {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    rotinas: Rotina[];
    diaInicial?: number;
    /** Depois de criar, levar a tela ao dia da Rotina nova. */
    onAbrirDia?: (dia: number) => void;
}) {
    const { toast } = useToast();
    const queryClient = useQueryClient();
    const [data, setData] = useState('');
    const [dia, setDia] = useState<number>(diaInicial ?? 1);
    const [nome, setNome] = useState('');
    const [erro, setErro] = useState<string | null>(null);
    const [resultado, setResultado] = useState<RotinaImportResultado | null>(null);
    const [gravado, setGravado] = useState<{ data: string; dia: number } | null>(null);

    useEffect(() => {
        if (open) {
            setData('');
            setNome('');
            setErro(null);
            setResultado(null);
            setGravado(null);
            if (diaInicial !== undefined) setDia(diaInicial);
        }
    }, [open, diaInicial]);

    const escolherData = (valor: string) => {
        setData(valor);
        const d = diaDaSemana(valor);
        if (d !== undefined) setDia(d);
    };

    // O import não tem simulação: esta prévia reusa a lista de broadcasts para o gestor ver o dia antes de gravar.
    const previa = useQuery({
        queryKey: ['rotina-previa-dia', data],
        enabled: open && !!data && diaDaSemana(data) !== undefined,
        queryFn: async () => {
            const [a, m, d] = data.split('-').map(Number);
            const ref = new Date(a, m - 1, d);
            const r = await getBroadcastCalendar(limiteUtcDoDiaBRT(ref, 'inicio'), limiteUtcDoDiaBRT(ref, 'fim'), meuIdSeAdmin());
            return { grupos: r.items.filter((g) => diaBRT(g.scheduled_at) === data), truncated: r.truncated };
        },
    });

    const existentes = rotinas.filter((e) => e.dia_semana === dia);
    const diaDaData = diaDaSemana(data);
    const divergeDoDia = diaDaData !== undefined && diaDaData !== dia;

    const importar = useMutation({
        mutationFn: () => importarRotinaDeData({ data, dia_semana: dia, nome: nome.trim() }),
        onSuccess: (r) => {
            setResultado(r);
            setGravado({ data, dia });
            setErro(null);
            queryClient.invalidateQueries({ queryKey: ['rotinas-agendamento'] });
            queryClient.invalidateQueries({ queryKey: ['rotina-agendamento', r.rotina_id] });
            queryClient.invalidateQueries({ queryKey: ['rotina-agendamento-divergencia', r.rotina_id] });
            toast({ title: 'Rotina criada', description: 'Nasce desligada: ligue quando estiver conferida.' });
        },
        onError: (e) => setErro(mensagemDeErro(e, 'Não foi possível importar.')),
    });

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-h-[90vh] max-w-3xl overflow-y-auto">
                <DialogHeader>
                    <DialogTitle>Montar uma Rotina a partir de um dia</DialogTitle>
                    <DialogDescription>
                        Copia os grupos que você já montou em uma data e grava como molde de um dia da semana.
                    </DialogDescription>
                </DialogHeader>

                {resultado && gravado ? <Resultado r={resultado} data={gravado.data} dia={gravado.dia} /> : (
                    <div className="space-y-4">
                        <div className="grid gap-3 sm:grid-cols-3">
                            <label className="space-y-1 text-sm font-medium">
                                1. Data a copiar
                                <Input type="date" value={data} onChange={(e) => escolherData(e.target.value)} />
                                <span className="block text-xs font-normal tabular-nums text-muted-foreground">
                                    {diaDaData !== undefined
                                        ? `${porExtenso(data)} — ${DIAS_SEMANA[diaDaData]}`
                                        : 'Escolha uma data já montada.'}
                                </span>
                            </label>
                            <label className="space-y-1 text-sm font-medium">
                                2. Vale para
                                <select
                                    className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm font-normal"
                                    value={dia}
                                    onChange={(e) => setDia(Number(e.target.value))}
                                >
                                    {DIAS_SEMANA.map((n, i) => (
                                        <option key={i} value={i}>{n}{diaDaData === i ? ' (dia da data)' : ''}</option>
                                    ))}
                                </select>
                                <span className="block text-xs font-normal text-muted-foreground">Sugerido pelo dia da data.</span>
                            </label>
                            <label className="space-y-1 text-sm font-medium">
                                3. Nome da Rotina
                                <Input value={nome} maxLength={160} onChange={(e) => setNome(e.target.value)} placeholder={`Ex.: ${DIAS_SEMANA[dia]} · geral`} />
                                <span className="block text-xs font-normal text-muted-foreground">Diferencie das outras Rotinas do mesmo dia.</span>
                            </label>
                        </div>

                        {/* Dito ANTES de gravar: é a causa mais comum de grupo ficar de fora. */}
                        {divergeDoDia && (
                            <div className="flex gap-2.5 rounded-lg border border-orange-300 bg-orange-50 p-3 text-xs text-orange-900">
                                <AlertTriangle className="mt-px h-[18px] w-[18px] shrink-0" />
                                <div>
                                    <strong>
                                        {porExtenso(data)} é {DIAS_MINUSCULOS[diaDaData]}, mas a Rotina vale para{' '}
                                        {DIAS_MINUSCULOS[dia]}.
                                    </strong>{' '}
                                    Grupos sem página agendada no dia local vão ficar de fora.{' '}
                                    <button
                                        type="button"
                                        className="font-semibold underline underline-offset-2"
                                        onClick={() => setDia(diaDaData)}
                                    >
                                        Usar {DIAS_MINUSCULOS[diaDaData]}
                                    </button>
                                </div>
                            </div>
                        )}

                        {existentes.length > 0 && (
                            <Alert>
                                <Info className="h-4 w-4" />
                                <AlertTitle>Já existe Rotina para {DIAS_SEMANA[dia]}</AlertTitle>
                                <AlertDescription>
                                    {existentes.map((e) => `${e.nome}${e.ativa ? ' (no ar)' : ' (desligada)'}`).join(', ')}.{' '}
                                    A Rotina nova nasce desligada e não interfere.{' '}
                                    {existentes.some((e) => e.ativa)
                                        ? 'A nova pode entrar no ar junto com ela, desde que as duas não tenham a mesma página.'
                                        : 'Confira se não é a mesma que você quer religar, em vez de montar outra.'}
                                </AlertDescription>
                            </Alert>
                        )}

                        {data && (
                            <div className="rounded-lg border p-3 text-sm">
                                <p className="mb-1 font-medium">Grupos agendados em {porExtenso(data)} (horário de Brasília)</p>
                                {previa.isLoading && <Loader2 className="h-4 w-4 animate-spin" />}
                                {previa.isError && <p className="text-xs text-destructive">Não foi possível listar os grupos do dia.</p>}
                                {previa.data?.truncated && (
                                    <p className="mb-1 text-xs font-medium text-amber-700">
                                        A lista veio cortada pela API: há mais grupos nesse dia do que os mostrados.
                                    </p>
                                )}
                                {previa.data && previa.data.grupos.length === 0 && (
                                    <p className="text-xs text-muted-foreground">Nenhum grupo agendado nesse dia.</p>
                                )}
                                {previa.data && previa.data.grupos.length > 0 && (
                                    <ul className="max-h-48 space-y-1 overflow-y-auto text-xs">
                                        {previa.data.grupos.map((g) => (
                                            <li key={g.group_broadcast_id}>
                                                {g.title ?? g.name ?? `Grupo ${g.group_broadcast_id}`} · {g.total_pages} página(s)
                                            </li>
                                        ))}
                                    </ul>
                                )}
                                <p className="mt-2 text-xs text-muted-foreground">
                                    Prévia aproximada. O import decide o dia pela hora local de cada página e mostra o que
                                    deixou de fora depois de gravar.
                                </p>
                            </div>
                        )}

                        {erro && <p className="text-sm text-destructive">{erro}</p>}
                    </div>
                )}

                <DialogFooter>
                    {resultado && gravado ? (
                        <>
                            <Button variant="outline" onClick={() => onOpenChange(false)}>Fechar</Button>
                            <Button onClick={() => { onAbrirDia?.(gravado.dia); onOpenChange(false); }}>
                                Abrir a Rotina
                            </Button>
                        </>
                    ) : (
                        <>
                            <Button variant="outline" onClick={() => onOpenChange(false)} disabled={importar.isPending}>Cancelar</Button>
                            {/* Sem travar por Rotina no ar: a importada nasce desligada e não colide. */}
                            <Button onClick={() => importar.mutate()} disabled={!data || !nome.trim() || importar.isPending}>
                                {importar.isPending && <Loader2 className="mr-1 h-4 w-4 animate-spin" />}
                                Criar Rotina (desligada)
                            </Button>
                        </>
                    )}
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
